import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, ArrowLeft, Send, Loader2, Clock, Package, User,
  Phone, MapPin, CreditCard, X, Bell, CheckCircle2, Circle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Chat {
  id: string;
  restaurant_id: string;
  order_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_id: string | null;
  status: string;
  unread_restaurant: number;
  created_at: string;
  updated_at: string;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  sender_type: string;
  message: string;
  created_at: string;
}

interface OrderInfo {
  id: string;
  status: string;
  order_type: string;
  customer_name: string | null;
  customer_phone: string | null;
  total: number;
  subtotal: number;
  delivery_fee: number;
  delivery_address: string | null;
  payment_method: string | null;
  created_at: string;
  notes: string | null;
}

const fmt = (v: number) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  received: { label: "Recebido", color: "hsl(25, 95%, 53%)" },
  preparing: { label: "Em preparo", color: "hsl(45, 93%, 47%)" },
  ready: { label: "Pronto", color: "hsl(142, 71%, 45%)" },
  out_for_delivery: { label: "Saiu p/ entrega", color: "hsl(217, 91%, 60%)" },
  completed: { label: "Entregue", color: "hsl(142, 71%, 45%)" },
  cancelled: { label: "Cancelado", color: "hsl(0, 72%, 51%)" },
};

const PAYMENT_LABELS: Record<string, string> = {
  pix: "PIX", credit_card: "Crédito", debit_card: "Débito",
  cash: "Dinheiro", mercadopago: "Mercado Pago", mp_pix: "PIX (MP)",
  pay_on_delivery: "Na Entrega",
};

const ChatRestaurante = () => {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<any>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showOrderPanel, setShowOrderPanel] = useState(true);

  // Audio for notifications
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    notificationSound.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczFjlvmNTfq2QxEj1ynNfhr2AuDkBzo9jfrVwqCkR2pdrhr1soB0d5p93grVYkBEl8qd/grFMgAUt+q+DfrE8cAE6AreDerlEYAFCCsOHerlUTAFKEsuLfsVoOAFSGs+PgtWEJAFaIteTiuWgEAFiKt+XjvG4BAFqMuObkv3QAAFuOuufmwnoAAF2QvOjoxIAAAF+SvenpxoYAAGGUwOrqyIwAAGOWwevrzpIAAGWYw+zs0JgAAGeaxe3u0p4AAGmcx+7v1KQAAGufye/x1qoAAG2hzPDy2LAAAHCj0PH03bYAAHKm0vL13rwAAHSo1fP24cIAAHaq1/T35MgAAHit2fb46c4AAHqw3Pf57dQAAHyy3/j68NoAAH+15Pn7898AAIGz4fn89+QAAIOy4Pj79OkAAIWx3/f68+4AAIev3fX58fIAAImv3PP38PcA");
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load restaurant and chats
  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const { data: rest } = await supabase
        .from("restaurants")
        .select("*")
        .eq("owner_id", user.id)
        .maybeSingle();
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurant(rest);

      // Load all chats
      const { data: chatList } = await supabase
        .from("restaurant_chats" as any)
        .select("*")
        .eq("restaurant_id", rest.id)
        .order("updated_at", { ascending: false }) as any;
      setChats((chatList || []) as Chat[]);
      setLoading(false);
    };
    load();
  }, [navigate]);

  // Realtime: new chats + messages
  useEffect(() => {
    if (!restaurant) return;

    const chatsChannel = supabase
      .channel(`rest-chats-${restaurant.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "restaurant_chats",
        filter: `restaurant_id=eq.${restaurant.id}`,
      }, (payload) => {
        const newChat = payload.new as Chat;
        setChats((prev) => [newChat, ...prev.filter(c => c.id !== newChat.id)]);
        // Play sound + toast
        notificationSound.current?.play().catch(() => {});
        toast("💬 Novo chat de cliente!", {
          description: newChat.customer_name || "Cliente",
          duration: 6000,
        });
        // Browser notification
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("💬 Novo chat de cliente", {
            body: `${newChat.customer_name || "Cliente"} iniciou uma conversa`,
            icon: restaurant.logo_url || "/favicon.ico",
          });
        }
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "restaurant_chats",
        filter: `restaurant_id=eq.${restaurant.id}`,
      }, (payload) => {
        const updatedChat = payload.new as Chat;
        setChats((prev) => prev.map(c => c.id === updatedChat.id ? updatedChat : c).sort((a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        ));
      })
      .subscribe();

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => { supabase.removeChannel(chatsChannel); };
  }, [restaurant]);

  // Realtime: messages for selected chat
  useEffect(() => {
    if (!selectedChat) return;

    const channel = supabase
      .channel(`rest-chat-msgs-${selectedChat.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "restaurant_chat_messages",
        filter: `chat_id=eq.${selectedChat.id}`,
      }, (payload) => {
        const msg = payload.new as ChatMessage;
        setMessages((prev) => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        if (msg.sender_type === "customer") {
          notificationSound.current?.play().catch(() => {});
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedChat]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Select a chat
  const selectChat = async (chat: Chat) => {
    setSelectedChat(chat);
    setLoadingMessages(true);
    setOrderInfo(null);

    // Load messages
    const { data: msgs } = await supabase
      .from("restaurant_chat_messages" as any)
      .select("*")
      .eq("chat_id", chat.id)
      .order("created_at") as any;
    setMessages((msgs || []) as ChatMessage[]);

    // Mark as read
    await supabase
      .from("restaurant_chats" as any)
      .update({ unread_restaurant: 0 } as any)
      .eq("id", chat.id) as any;
    setChats((prev) => prev.map(c => c.id === chat.id ? { ...c, unread_restaurant: 0 } : c));

    // Load order info if available
    if (chat.order_id) {
      const { data: order } = await supabase
        .from("orders")
        .select("*")
        .eq("id", chat.order_id)
        .maybeSingle();
      if (order) setOrderInfo(order as unknown as OrderInfo);
    }

    setLoadingMessages(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedChat || sending) return;
    const text = newMessage.trim();
    setNewMessage("");
    setSending(true);
    try {
      await supabase.from("restaurant_chat_messages" as any).insert({
        chat_id: selectedChat.id,
        sender_type: "restaurant",
        message: text,
      } as any);
      // Increment unread for customer
      await supabase.from("restaurant_chats" as any)
        .update({ unread_customer: (selectedChat as any).unread_customer + 1, updated_at: new Date().toISOString() } as any)
        .eq("id", selectedChat.id) as any;
    } catch (err) {
      console.error("Send error:", err);
      setNewMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const closeChat = async (chatId: string) => {
    await supabase.from("restaurant_chats" as any).update({ status: "closed" } as any).eq("id", chatId) as any;
    setChats((prev) => prev.map(c => c.id === chatId ? { ...c, status: "closed" } : c));
    if (selectedChat?.id === chatId) {
      setSelectedChat(null);
      setMessages([]);
      setOrderInfo(null);
    }
    toast.success("Chat encerrado");
  };

  const totalUnread = chats.reduce((sum, c) => sum + (c.unread_restaurant || 0), 0);
  const elapsedMinutes = (chat: Chat) => {
    return Math.floor((Date.now() - new Date(chat.created_at).getTime()) / 60000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <div className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card shrink-0">
        <button onClick={() => navigate("/dashboard")} className="p-2 rounded-lg hover:bg-secondary transition-colors">
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <MessageCircle className="h-5 w-5 text-primary" />
        <h1 className="font-bold text-foreground">Chat com Clientes</h1>
        {totalUnread > 0 && (
          <span className="ml-auto bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
            {totalUnread}
          </span>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Chat list */}
        <div className={`${selectedChat ? "hidden md:flex" : "flex"} w-full md:w-80 lg:w-96 border-r border-border flex-col bg-card`}>
          <div className="p-3 border-b border-border">
            <p className="text-xs text-muted-foreground font-medium">{chats.length} conversa(s)</p>
          </div>
          <div className="flex-1 overflow-auto">
            {chats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <MessageCircle className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Nenhum chat ainda</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Os chats dos clientes aparecerão aqui</p>
              </div>
            ) : (
              chats.map((chat) => {
                const isSelected = selectedChat?.id === chat.id;
                const mins = elapsedMinutes(chat);
                return (
                  <button
                    key={chat.id}
                    onClick={() => selectChat(chat)}
                    className={`w-full flex items-start gap-3 px-4 py-3 border-b border-border/50 text-left transition-colors hover:bg-secondary/50 ${
                      isSelected ? "bg-primary/5 border-l-2 border-l-primary" : ""
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {chat.customer_name || "Cliente"}
                        </p>
                        {chat.unread_restaurant > 0 && (
                          <span className="bg-primary text-primary-foreground text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0">
                            {chat.unread_restaurant}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        Pedido #{chat.order_id?.slice(0, 8).toUpperCase() || "—"}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Clock className="h-3 w-3" /> {mins < 60 ? `${mins}min` : `${Math.floor(mins / 60)}h`}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          chat.status === "open"
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {chat.status === "open" ? "Aberto" : "Encerrado"}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat conversation */}
        {selectedChat ? (
          <div className="flex-1 flex flex-col">
            {/* Chat header */}
            <div className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card shrink-0">
              <button onClick={() => setSelectedChat(null)} className="md:hidden p-1.5 rounded-lg hover:bg-secondary">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{selectedChat.customer_name || "Cliente"}</p>
                {selectedChat.customer_phone && (
                  <p className="text-xs text-muted-foreground">{selectedChat.customer_phone}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setShowOrderPanel(!showOrderPanel)}
                  className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
                  title="Detalhes do pedido"
                >
                  <Package className="h-4 w-4" />
                </button>
                {selectedChat.status === "open" && (
                  <button
                    onClick={() => closeChat(selectedChat.id)}
                    className="px-3 py-1.5 rounded-lg bg-destructive/10 text-destructive text-xs font-semibold hover:bg-destructive/20 transition-colors"
                  >
                    Encerrar
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Messages area */}
              <div className="flex-1 flex flex-col">
                <div className="flex-1 overflow-auto p-4 space-y-3 bg-secondary/20">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <>
                      {messages.map((msg) => {
                        const isRestaurant = msg.sender_type === "restaurant";
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${isRestaurant ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm ${
                                isRestaurant
                                  ? "bg-primary text-primary-foreground rounded-br-md"
                                  : "bg-card text-foreground border border-border rounded-bl-md"
                              }`}
                            >
                              <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                              <p className={`text-[10px] mt-1 ${isRestaurant ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                                {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Input */}
                {selectedChat.status === "open" ? (
                  <div className="p-3 border-t border-border bg-card flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Responder ao cliente..."
                      className="flex-1 h-10 rounded-full bg-secondary px-4 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />
                    <button
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                      className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-all active:scale-90"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                ) : (
                  <div className="p-3 border-t border-border bg-muted text-center">
                    <p className="text-xs text-muted-foreground">Chat encerrado</p>
                  </div>
                )}
              </div>

              {/* Order details sidebar */}
              {showOrderPanel && orderInfo && (
                <div className="hidden lg:flex w-72 border-l border-border flex-col bg-card overflow-auto">
                  <div className="p-4 space-y-4">
                    <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" /> Detalhes do Pedido
                    </h3>

                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <Circle className="h-3 w-3" style={{ color: STATUS_LABELS[orderInfo.status]?.color, fill: STATUS_LABELS[orderInfo.status]?.color }} />
                      <span className="text-sm font-medium" style={{ color: STATUS_LABELS[orderInfo.status]?.color }}>
                        {STATUS_LABELS[orderInfo.status]?.label || orderInfo.status}
                      </span>
                    </div>

                    {/* Customer */}
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Cliente</p>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-foreground">{orderInfo.customer_name || "—"}</span>
                      </div>
                      {orderInfo.customer_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">{orderInfo.customer_phone}</span>
                        </div>
                      )}
                    </div>

                    {/* Time info */}
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Horário</p>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {new Date(orderInfo.created_at).toLocaleString("pt-BR", {
                            day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>⏱️ Esperando há {Math.floor((Date.now() - new Date(orderInfo.created_at).getTime()) / 60000)} min</span>
                      </div>
                    </div>

                    {/* Delivery address */}
                    {orderInfo.delivery_address && (
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Endereço</p>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{orderInfo.delivery_address}</span>
                        </div>
                      </div>
                    )}

                    {/* Payment */}
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Pagamento</p>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {PAYMENT_LABELS[orderInfo.payment_method || ""] || orderInfo.payment_method || "—"}
                        </span>
                      </div>
                    </div>

                    {/* Values */}
                    <div className="space-y-1.5 p-3 rounded-xl bg-secondary">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="text-foreground tabular-nums">{fmt(orderInfo.subtotal)}</span>
                      </div>
                      {Number(orderInfo.delivery_fee) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Entrega</span>
                          <span className="text-foreground tabular-nums">{fmt(orderInfo.delivery_fee)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-border">
                        <span className="text-foreground">Total</span>
                        <span className="text-foreground tabular-nums">{fmt(orderInfo.total)}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {orderInfo.notes && (
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Observações</p>
                        <p className="text-sm text-foreground whitespace-pre-wrap bg-secondary p-2.5 rounded-lg">{orderInfo.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-secondary/10">
            <div className="text-center">
              <MessageCircle className="h-16 w-16 text-muted-foreground/20 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">Selecione um chat</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Escolha uma conversa para responder</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatRestaurante;
